import { useState } from 'react';
import Layout from '../../../components/layout/Layout';
import { WizardSteps } from '../../../components/simulador/WizardSteps';
import { ProdutoServicoSelector } from '../../../components/simulador/ProdutoServicoSelector';
import { ParticipantesSelector } from '../../../components/simulador/ParticipantesSelector';
import { EditableSimulacaoPreview } from '../../../components/simulador/EditableSimulacaoPreview';
import { CustoAnalyzer } from '../../../components/simulador/CustoAnalyzer';
import { IAInsights } from '../../../components/simulador/IAInsights';
import { AnaliseInteligente } from '../../../components/simulador/AnaliseInteligente';
import { ConfiguracaoSimulador, TipoCampanha, ProdutoServicoSimulador, ParticipanteSimulador, CampanhaSimulada } from '../../../types/simulador';
import { gerarCampanhaCompleta } from '../../../services/simuladorIA';
import { obterRecomendacoesEvolutivas, adicionarCampanhaAoHistorico } from '../../../services/campanhaAnalyzer';
import { calcularCustos, calcularComplexidade } from '../../../services/simuladorIA';

const ETAPAS = [
  'Campanha Base',
  'Tipo de Campanha',
  'Produtos/Serviços',
  'Participantes',
  'Configuração IA',
  'Geração IA',
  'Revisão',
  'Análise Inteligente',
  'Análise de Custos',
  'Sugestões'
];

// Mock de campanhas existentes
const CAMPANHAS_EXISTENTES = [
  {
    id: 1,
    nome: 'Vendas Q4 2024',
    tipo: 'Vendas',
    participantes: 45,
    inicio: '01/10/2024',
    fim: '31/12/2024',
    status: 'Ativa',
    descricao: 'Campanha focada em aumentar as vendas do último trimestre de 2024',
    produtosServicos: [
      { id: 'prod-1', codigo: 'PROD001', descricao: 'Notebook Dell Inspiron 15', tipo: 'produto' as const, pesoComercial: 'Alto' as const, perfilEconomico: 'Tolerante' as const, valorReferencia: 3500 },
      { id: 'prod-2', codigo: 'PROD002', descricao: 'Mouse Logitech MX Master', tipo: 'produto' as const, pesoComercial: 'Médio' as const, perfilEconomico: 'Equilibrado' as const, valorReferencia: 450 },
      { id: 'prod-3', codigo: 'SERV001', descricao: 'Consultoria em TI', tipo: 'servico' as const, pesoComercial: 'Estratégico' as const, perfilEconomico: 'Estratégico' as const, valorReferencia: 8000 }
    ],
    participantesVinculados: [
      { id: '1', nome: 'João Silva', cpf: '123.456.789-00', email: 'joao@empresa.com', departamento: 'Vendas', cargo: 'Vendedor', selecionado: true },
      { id: '2', nome: 'Maria Santos', cpf: '234.567.890-11', email: 'maria@empresa.com', departamento: 'Vendas', cargo: 'Vendedor', selecionado: true },
      { id: '3', nome: 'Pedro Oliveira', cpf: '345.678.901-22', email: 'pedro@empresa.com', departamento: 'Vendas', cargo: 'Supervisor', selecionado: true }
    ],
    metas: [
      { id: 'm1', descricao: 'Vender 10 unidades de Notebook Dell', tipo: 'volume', valorMeta: 10, pontos: 500, nivel: 'Bronze' },
      { id: 'm2', descricao: 'Vender 20 unidades de Notebook Dell', tipo: 'volume', valorMeta: 20, pontos: 1200, nivel: 'Prata' },
      { id: 'm3', descricao: 'Atingir R$ 50.000 em vendas', tipo: 'valor', valorMeta: 50000, pontos: 2000, nivel: 'Ouro' }
    ],
    distintivos: [
      { id: 'd1', nome: 'Primeiro Passo', descricao: 'Primeira venda realizada', condicao: 'Realizar primeira venda', xp: 100, icone: 'ri-footprint-line', cor: 'blue' },
      { id: 'd2', nome: 'Vendedor Consistente', descricao: 'Vendas por 7 dias consecutivos', condicao: '7 dias consecutivos vendendo', xp: 500, icone: 'ri-calendar-check-line', cor: 'green' }
    ],
    progressao: {
      niveis: [
        { nivel: 1, nome: 'Iniciante', xpNecessario: 0, xpProximo: 1000 },
        { nivel: 2, nome: 'Aprendiz', xpNecessario: 1000, xpProximo: 2500 },
        { nivel: 3, nome: 'Profissional', xpNecessario: 2500, xpProximo: 5000 }
      ],
      missoes: [
        { id: 'mis1', titulo: 'Venda do Dia', descricao: 'Realizar pelo menos 1 venda hoje', tipo: 'diaria', xp: 50, nivel: 'Bronze' },
        { id: 'mis2', titulo: 'Meta Semanal', descricao: 'Realizar 5 vendas na semana', tipo: 'semanal', xp: 200, nivel: 'Prata' }
      ]
    }
  },
  {
    id: 2,
    nome: 'Meta Dezembro',
    tipo: 'Performance',
    participantes: 32,
    inicio: '01/12/2024',
    fim: '31/12/2024',
    status: 'Ativa',
    descricao: 'Campanha de performance para atingir as metas de dezembro',
    produtosServicos: [
      { id: 'prod-4', codigo: 'PROD003', descricao: 'Teclado Mecânico Keychron', tipo: 'produto' as const, pesoComercial: 'Médio' as const, perfilEconomico: 'Equilibrado' as const, valorReferencia: 650 },
      { id: 'prod-5', codigo: 'SERV002', descricao: 'Suporte Técnico Premium', tipo: 'servico' as const, pesoComercial: 'Alto' as const, perfilEconomico: 'Tolerante' as const, valorReferencia: 2500 }
    ],
    participantesVinculados: [
      { id: '4', nome: 'Ana Costa', cpf: '456.789.012-33', email: 'ana@empresa.com', departamento: 'Comercial', cargo: 'Vendedor', selecionado: true },
      { id: '5', nome: 'Carlos Ferreira', cpf: '567.890.123-44', email: 'carlos@empresa.com', departamento: 'Comercial', cargo: 'Gerente', selecionado: true }
    ],
    metas: [
      { id: 'm4', descricao: 'Vender 15 Teclados Mecânicos', tipo: 'volume', valorMeta: 15, pontos: 800, nivel: 'Bronze' },
      { id: 'm5', descricao: 'Fechar 3 contratos de Suporte Premium', tipo: 'volume', valorMeta: 3, pontos: 1500, nivel: 'Ouro' }
    ],
    distintivos: [
      { id: 'd3', nome: 'Top Performer', descricao: 'Estar no top 3 do mês', condicao: 'Top 3 do ranking mensal', xp: 2000, icone: 'ri-trophy-line', cor: 'yellow' }
    ],
    progressao: {
      niveis: [
        { nivel: 1, nome: 'Novato', xpNecessario: 0, xpProximo: 800 },
        { nivel: 2, nome: 'Experiente', xpNecessario: 800, xpProximo: 2000 }
      ],
      missoes: [
        { id: 'mis3', titulo: 'Diversificação', descricao: 'Vender 2 produtos diferentes no dia', tipo: 'diaria', xp: 75, nivel: 'Prata' }
      ]
    }
  },
  {
    id: 3,
    nome: 'Captação Novos Clientes',
    tipo: 'Aquisição',
    participantes: 28,
    inicio: '15/11/2024',
    fim: '15/01/2025',
    status: 'Ativa',
    descricao: 'Foco na captação de novos clientes para expandir a base',
    produtosServicos: [
      { id: 'prod-6', codigo: 'SERV003', descricao: 'Plano Básico SaaS', tipo: 'servico' as const, pesoComercial: 'Baixo' as const, perfilEconomico: 'Sensível' as const, valorReferencia: 199 },
      { id: 'prod-7', codigo: 'SERV004', descricao: 'Plano Enterprise SaaS', tipo: 'servico' as const, pesoComercial: 'Estratégico' as const, perfilEconomico: 'Estratégico' as const, valorReferencia: 1999 }
    ],
    participantesVinculados: [
      { id: '6', nome: 'Juliana Lima', cpf: '678.901.234-55', email: 'juliana@empresa.com', departamento: 'Vendas', cargo: 'Vendedor', selecionado: true },
      { id: '7', nome: 'Roberto Alves', cpf: '789.012.345-66', email: 'roberto@empresa.com', departamento: 'Comercial', cargo: 'Vendedor', selecionado: true }
    ],
    metas: [
      { id: 'm6', descricao: 'Captar 10 novos clientes', tipo: 'volume', valorMeta: 10, pontos: 1000, nivel: 'Bronze' },
      { id: 'm7', descricao: 'Fechar 2 contratos Enterprise', tipo: 'volume', valorMeta: 2, pontos: 3000, nivel: 'Diamante' }
    ],
    distintivos: [
      { id: 'd4', nome: 'Caçador de Talentos', descricao: 'Captar 5 novos clientes em uma semana', condicao: '5 novos clientes em 7 dias', xp: 800, icone: 'ri-user-add-line', cor: 'purple' }
    ],
    progressao: {
      niveis: [
        { nivel: 1, nome: 'Prospector', xpNecessario: 0, xpProximo: 1200 },
        { nivel: 2, nome: 'Hunter', xpNecessario: 1200, xpProximo: 3000 }
      ],
      missoes: [
        { id: 'mis4', titulo: 'Primeiro Contato', descricao: 'Realizar 3 ligações para prospects', tipo: 'diaria', xp: 30, nivel: 'Bronze' }
      ]
    }
  }
];

export default function SimuladorIAPage() {
  const [config, setConfig] = useState<ConfiguracaoSimulador>({
    etapaAtual: 1,
    totalEtapas: 10,
    dadosBasicos: null,
    produtosServicosSelecionados: [],
    participantesSelecionados: [],
    campanhaGerada: null
  });

  const [gerando, setGerando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // NOVO: Campanha base selecionada
  const [campanhaBaseSelecionada, setCampanhaBaseSelecionada] = useState<number | null>(null);
  const [modoCriacao, setModoCriacao] = useState<'nova' | 'baseada'>('nova');

  // NOVO: Estados para erros de validação
  const [erros, setErros] = useState<{
    etapa1?: string;
    etapa2?: { nome?: string; dataInicio?: string; dataFim?: string };
    etapa3?: string;
    etapa4?: string;
    etapa5?: string;
  }>({});

  // Etapa 2: Dados Básicos (antiga etapa 1)
  const [formBasico, setFormBasico] = useState({
    nome: '',
    descricao: '',
    tipo: 'Produtos' as TipoCampanha,
    dataInicio: '',
    dataFim: ''
  });

  // Configuração do que será gerado pela IA
  const [configIA, setConfigIA] = useState({
    gerarMetas: true,
    gerarDistintivos: true,
    gerarProgressao: true,
    // NOVAS configurações avançadas
    quantidadeMetas: 5,
    quantidadeDistintivos: 6,
    quantidadeMissoesDiarias: 3,
    quantidadeMissoesSemanais: 2,
    quantidadeMissoesMensais: 1,
    quantidadeNiveis: 10,
    dificuldade: 'Equilibrada' as 'Fácil' | 'Equilibrada' | 'Desafiadora',
    focoRecompensas: 'Balanceado' as 'Pontos' | 'XP' | 'Balanceado',
    periodoMissoes: 'Completo' as 'Completo' | 'Apenas Diárias' | 'Apenas Semanais' | 'Diárias e Semanais'
  });

  // Função para importar dados da campanha selecionada
  const handleImportarCampanha = (campanhaId: number) => {
    const campanha = CAMPANHAS_EXISTENTES.find(c => c.id === campanhaId);
    if (!campanha) return;

    // Importar dados básicos
    setFormBasico({
      nome: `${campanha.nome} - Nova Versão`,
      descricao: campanha.descricao,
      tipo: campanha.produtosServicos.some(p => p.tipo === 'servico') && campanha.produtosServicos.some(p => p.tipo === 'produto') 
        ? 'Mista' 
        : campanha.produtosServicos[0]?.tipo === 'servico' ? 'Serviços' : 'Produtos',
      dataInicio: '',
      dataFim: ''
    });

    // Importar produtos/serviços
    setConfig(prev => ({
      ...prev,
      produtosServicosSelecionados: campanha.produtosServicos,
      participantesSelecionados: campanha.participantesVinculados
    }));

    setCampanhaBaseSelecionada(campanhaId);
  };

  const handleProximaEtapa = () => {
    if (config.etapaAtual === 1) {
      // Etapa 1: Seleção de campanha base
      if (modoCriacao === 'baseada' && !campanhaBaseSelecionada) {
        setErros({ etapa1: 'Selecione uma campanha base ou escolha criar uma nova campanha' });
        return;
      }
      setErros({});
      setConfig({ ...config, etapaAtual: 2 });
    } else if (config.etapaAtual === 2) {
      // Etapa 2: Dados Básicos
      const novosErros: { nome?: string; dataInicio?: string; dataFim?: string } = {};
      
      if (!formBasico.nome) {
        novosErros.nome = 'O nome da campanha é obrigatório';
      }
      if (!formBasico.dataInicio) {
        novosErros.dataInicio = 'A data de início é obrigatória';
      }
      if (!formBasico.dataFim) {
        novosErros.dataFim = 'A data de término é obrigatória';
      }
      if (formBasico.dataInicio && formBasico.dataFim && formBasico.dataInicio > formBasico.dataFim) {
        novosErros.dataFim = 'A data de término deve ser posterior à data de início';
      }

      if (Object.keys(novosErros).length > 0) {
        setErros({ etapa2: novosErros });
        return;
      }

      setErros({});
      setConfig({
        ...config,
        dadosBasicos: formBasico,
        etapaAtual: 3
      });
    } else if (config.etapaAtual === 3) {
      // Etapa 3: Produtos/Serviços
      if (config.produtosServicosSelecionados.length === 0) {
        setErros({ etapa3: 'Adicione pelo menos um produto ou serviço' });
        return;
      }
      setErros({});
      setConfig({ ...config, etapaAtual: 4 });
    } else if (config.etapaAtual === 4) {
      // Etapa 4: Participantes
      if (config.participantesSelecionados.length === 0) {
        setErros({ etapa4: 'Selecione pelo menos um participante' });
        return;
      }
      setErros({});
      setConfig({ ...config, etapaAtual: 5 });
    } else if (config.etapaAtual === 5) {
      // Etapa 5: Configuração IA
      if (!configIA.gerarMetas && !configIA.gerarDistintivos && !configIA.gerarProgressao) {
        setErros({ etapa5: 'Selecione pelo menos uma opção para gerar' });
        return;
      }
      setErros({});
      setConfig({ ...config, etapaAtual: 6 });
    } else if (config.etapaAtual === 6) {
      // Etapa 6: Geração IA
      handleGerarCampanha();
    } else if (config.etapaAtual < 10) {
      setConfig({ ...config, etapaAtual: config.etapaAtual + 1 });
    }
  };

  const handleVoltarEtapa = () => {
    if (config.etapaAtual > 1) {
      setErros({});
      setConfig({ ...config, etapaAtual: config.etapaAtual - 1 });
    }
  };

  const handleGerarCampanha = () => {
    if (!config.dadosBasicos) return;

    setGerando(true);
    
    // Simula processamento da IA
    setTimeout(() => {
      const campanhaGerada = gerarCampanhaCompleta(
        config.dadosBasicos!,
        config.produtosServicosSelecionados,
        config.participantesSelecionados,
        configIA
      );

      // Se baseado em campanha existente, mesclar dados
      if (campanhaBaseSelecionada) {
        const campanhaBase = CAMPANHAS_EXISTENTES.find(c => c.id === campanhaBaseSelecionada);
        if (campanhaBase) {
          // Mesclar metas existentes com as novas geradas
          if (campanhaBase.metas && configIA.gerarMetas) {
            campanhaGerada.metas = [...campanhaBase.metas, ...campanhaGerada.metas];
          } else if (campanhaBase.metas && !configIA.gerarMetas) {
            campanhaGerada.metas = campanhaBase.metas;
          }

          // Mesclar distintivos existentes com os novos gerados
          if (campanhaBase.distintivos && configIA.gerarDistintivos) {
            campanhaGerada.distintivos = [...campanhaBase.distintivos, ...campanhaGerada.distintivos];
          } else if (campanhaBase.distintivos && !configIA.gerarDistintivos) {
            campanhaGerada.distintivos = campanhaBase.distintivos;
          }

          // Mesclar progressão existente com a nova gerada
          if (campanhaBase.progressao && configIA.gerarProgressao) {
            // Verificar se campanhaGerada.progressao existe antes de mesclar
            if (campanhaGerada.progressao) {
              campanhaGerada.progressao = {
                niveis: [...campanhaBase.progressao.niveis, ...campanhaGerada.progressao.niveis],
                missoes: [...campanhaBase.progressao.missoes, ...campanhaGerada.progressao.missoes]
              };
            } else {
              // Se não existe, usar apenas a progressão da campanha base
              campanhaGerada.progressao = campanhaBase.progressao;
            }
          } else if (campanhaBase.progressao && !configIA.gerarProgressao) {
            campanhaGerada.progressao = campanhaBase.progressao;
          }
        }
      }

      // Adiciona sugestões evolutivas
      const sugestoes = obterRecomendacoesEvolutivas(campanhaGerada);
      campanhaGerada.sugestoesEvolutivas = sugestoes;

      setConfig({
        ...config,
        campanhaGerada,
        etapaAtual: 7
      });
      setGerando(false);
    }, 2000);
  };

  const handleSalvarSimulacao = () => {
    if (!config.campanhaGerada) return;

    setSalvando(true);
    
    setTimeout(() => {
      adicionarCampanhaAoHistorico(config.campanhaGerada!);
      alert('Simulação salva com sucesso!');
      setSalvando(false);
    }, 1000);
  };

  const handleCriarCampanha = () => {
    if (!config.campanhaGerada) return;

    if (confirm('Deseja criar esta campanha no sistema?')) {
      adicionarCampanhaAoHistorico(config.campanhaGerada);
      alert('Campanha criada com sucesso! Você será redirecionado para a lista de campanhas.');
      window.location.href = '/admin/incentivo/campanhas';
    }
  };

  const handleNovaSimulacao = () => {
    if (confirm('Deseja iniciar uma nova simulação? Os dados atuais serão perdidos.')) {
      setConfig({
        etapaAtual: 1,
        totalEtapas: 10,
        dadosBasicos: null,
        produtosServicosSelecionados: [],
        participantesSelecionados: [],
        campanhaGerada: null
      });
      setFormBasico({
        nome: '',
        descricao: '',
        tipo: 'Produtos',
        dataInicio: '',
        dataFim: ''
      });
      setConfigIA({
        gerarMetas: true,
        gerarDistintivos: true,
        gerarProgressao: true
      });
      setCampanhaBaseSelecionada(null);
      setModoCriacao('nova');
    }
  };

  const handleCampanhaChange = (campanhaAtualizada: CampanhaSimulada) => {
    // Recalcular custos e complexidade
    const novosCustos = calcularCustos(
      campanhaAtualizada.recompensas,
      campanhaAtualizada.participantes,
      campanhaAtualizada.produtosServicos
    );

    const novaComplexidade = calcularComplexidade(campanhaAtualizada);

    const campanhaComRecalculo = {
      ...campanhaAtualizada,
      analiseCustos: novosCustos,
      analiseComplexidade: novaComplexidade
    };

    setConfig({
      ...config,
      campanhaGerada: campanhaComRecalculo
    });
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <i className="ri-brain-line text-orange-600"></i>
                Simulador Inteligente de Campanhas
              </h1>
              <p className="text-gray-600 mt-1">
                Crie campanhas de incentivo com inteligência artificial
              </p>
            </div>
            {config.campanhaGerada && (
              <button
                onClick={handleNovaSimulacao}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Nova Simulação
              </button>
            )}
          </div>
        </div>

        {/* Aviso de Compliance */}
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-orange-600 text-xl mt-0.5"></i>
          <div className="flex-1">
            <p className="text-sm text-orange-800">
              ⚠️ Esta simulação utiliza inteligência artificial, campos abstratos de Peso Comercial e Perfil Econômico 
              e recompensas reais do catálogo interno, sem uso de dados financeiros sensíveis.
            </p>
          </div>
        </div>

        {/* Wizard Steps */}
        <WizardSteps
          etapaAtual={config.etapaAtual}
          totalEtapas={config.totalEtapas}
          etapas={ETAPAS}
        />

        {/* Conteúdo das Etapas */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* NOVA Etapa 1: Seleção de Campanha Base */}
          {config.etapaAtual === 1 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Escolha o Modo de Criação</h2>
              
              {/* Seleção do Modo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 max-w-4xl">
                <div
                  onClick={() => {
                    setModoCriacao('nova');
                    setCampanhaBaseSelecionada(null);
                  }}
                  className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                    modoCriacao === 'nova'
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-300 hover:border-orange-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      modoCriacao === 'nova'
                      ? 'bg-orange-500'
                      : 'bg-gray-300'
                    }`}>
                      <i className="ri-add-circle-line text-white text-2xl"></i>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      modoCriacao === 'nova'
                        ? 'border-orange-500 bg-orange-500'
                        : 'border-gray-300'
                    }`}>
                      {modoCriacao === 'nova' && (
                        <i className="ri-check-line text-white text-sm"></i>
                      )}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Criar Nova Campanha</h3>
                  <p className="text-sm text-gray-600">
                    Comece do zero e configure todos os elementos da campanha com a ajuda da IA
                  </p>
                </div>

                <div
                  onClick={() => setModoCriacao('baseada')}
                  className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                    modoCriacao === 'baseada'
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      modoCriacao === 'baseada' ? 'bg-blue-500' : 'bg-gray-300'
                    }`}>
                      <i className="ri-file-copy-line text-white text-2xl"></i>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      modoCriacao === 'baseada'
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {modoCriacao === 'baseada' && (
                        <i className="ri-check-line text-white text-sm"></i>
                      )}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Baseada em Campanha Existente</h3>
                  <p className="text-sm text-gray-600">
                    Importe dados de uma campanha existente e gere novas opções de engajamento com IA
                  </p>
                </div>
              </div>

              {/* Lista de Campanhas Existentes */}
              {modoCriacao === 'baseada' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Selecione uma Campanha Base</h3>
                  <div className="space-y-4 max-w-5xl">
                    {CAMPANHAS_EXISTENTES.map((campanha) => (
                      <div
                        key={campanha.id}
                        onClick={() => handleImportarCampanha(campanha.id)}
                        className={`border-2 rounded-lg p-5 cursor-pointer transition-all ${
                          campanhaBaseSelecionada === campanha.id
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="text-lg font-bold text-gray-900">{campanha.nome}</h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                campanha.tipo === 'Vendas' ? 'bg-blue-100 text-blue-800' :
                                campanha.tipo === 'Performance' ? 'bg-orange-100 text-orange-800' :
                                'bg-emerald-100 text-emerald-800'
                              }`}>
                                {campanha.tipo}
                              </span>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                {campanha.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{campanha.descricao}</p>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <i className="ri-group-line text-gray-500"></i>
                                <span>{campanha.participantes} participantes</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <i className="ri-calendar-line text-gray-500"></i>
                                <span>{campanha.inicio} - {campanha.fim}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <i className="ri-shopping-bag-line text-gray-500"></i>
                                <span>{campanha.produtosServicos.length} produtos/serviços</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <i className="ri-target-line text-gray-500"></i>
                                <span>{campanha.metas?.length || 0} metas</span>
                              </div>
                            </div>

                            {/* Resumo do que será importado */}
                            {campanhaBaseSelecionada === campanha.id && (
                              <div className="mt-4 p-3 bg-blue-100 border border-blue-300 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <i className="ri-information-line text-blue-700 mt-0.5"></i>
                                  <div className="flex-1">
                                    <p className="text-sm font-semibold text-blue-900 mb-2">Dados que serão importados:</p>
                                    <div className="grid grid-cols-2 gap-2 text-xs text-blue-800">
                                      <div className="flex items-center gap-1">
                                        <i className="ri-check-line text-blue-600"></i>
                                        <span>{campanha.produtosServicos.length} Produtos/Serviços</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <i className="ri-check-line text-blue-600"></i>
                                        <span>{campanha.participantesVinculados.length} Participantes</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <i className="ri-check-line text-blue-600"></i>
                                        <span>{campanha.metas?.length || 0} Metas existentes</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <i className="ri-check-line text-blue-600"></i>
                                        <span>{campanha.distintivos?.length || 0} Distintivos existentes</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <i className="ri-check-line text-blue-600"></i>
                                        <span>{campanha.progressao?.niveis.length || 0} Níveis de progressão</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <i className="ri-check-line text-blue-600"></i>
                                        <span>{campanha.progressao?.missoes.length || 0} Missões existentes</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-4 ${
                            campanhaBaseSelecionada === campanha.id
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-gray-300'
                          }`}>
                            {campanhaBaseSelecionada === campanha.id && (
                              <i className="ri-check-line text-white"></i>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Erro de validação */}
                  {erros.etapa1 && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 max-w-5xl">
                      <i className="ri-error-warning-line text-red-600 text-lg mt-0.5"></i>
                      <p className="text-sm text-red-800">{erros.etapa1}</p>
                    </div>
                  )}

                  {/* Informação sobre a IA */}
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg max-w-5xl">
                    <div className="flex items-start gap-3">
                      <i className="ri-lightbulb-line text-green-600 text-xl mt-0.5"></i>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-green-900 mb-2">💡 Como funciona?</h4>
                        <div className="space-y-1 text-sm text-green-800">
                          <p>• A IA importará automaticamente os produtos, serviços e participantes da campanha selecionada</p>
                          <p>• Você poderá escolher se deseja manter as metas, distintivos e progressões existentes</p>
                          <p>• A IA gerará novas opções de engajamento baseadas nos dados importados</p>
                          <p>• Todos os elementos poderão ser editados e ajustados antes de criar a campanha</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Etapa 2: Dados Básicos (antiga etapa 1) */}
          {config.etapaAtual === 2 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Informações Básicas da Campanha</h2>
              
              {/* Indicador de campanha base */}
              {campanhaBaseSelecionada && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-blue-900 mb-1">
                        📋 Baseado na campanha: {CAMPANHAS_EXISTENTES.find(c => c.id === campanhaBaseSelecionada)?.nome}
                      </p>
                      <p className="text-sm text-blue-800">
                        Os dados foram pré-preenchidos. Você pode ajustá-los conforme necessário.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4 max-w-2xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Campanha *
                  </label>
                  <input
                    type="text"
                    value={formBasico.nome}
                    onChange={(e) => {
                      setFormBasico({ ...formBasico, nome: e.target.value });
                      if (erros.etapa2?.nome) {
                        setErros({ ...erros, etapa2: { ...erros.etapa2, nome: undefined } });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      erros.etapa2?.nome ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Campanha Primavera 2024"
                  />
                  {erros.etapa2?.nome && (
                    <div className="mt-1 flex items-start gap-2">
                      <i className="ri-error-warning-line text-red-600 text-sm mt-0.5"></i>
                      <p className="text-sm text-red-600">{erros.etapa2.nome}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    value={formBasico.descricao}
                    onChange={(e) => setFormBasico({ ...formBasico, descricao: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    rows={3}
                    placeholder="Descreva os objetivos da campanha..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Campanha *
                  </label>
                  <select
                    value={formBasico.tipo}
                    onChange={(e) => setFormBasico({ ...formBasico, tipo: e.target.value as TipoCampanha })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="Produtos">Venda de Produtos</option>
                    <option value="Serviços">Venda de Serviços</option>
                    <option value="Mista">Produtos e Serviços</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Início *
                    </label>
                    <input
                      type="date"
                      value={formBasico.dataInicio}
                      onChange={(e) => {
                        setFormBasico({ ...formBasico, dataInicio: e.target.value });
                        if (erros.etapa2?.dataInicio) {
                          setErros({ ...erros, etapa2: { ...erros.etapa2, dataInicio: undefined } });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                        erros.etapa2?.dataInicio ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {erros.etapa2?.dataInicio && (
                      <div className="mt-1 flex items-start gap-2">
                        <i className="ri-error-warning-line text-red-600 text-sm mt-0.5"></i>
                        <p className="text-sm text-red-600">{erros.etapa2.dataInicio}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Término *
                    </label>
                    <input
                      type="date"
                      value={formBasico.dataFim}
                      onChange={(e) => {
                        setFormBasico({ ...formBasico, dataFim: e.target.value });
                        if (erros.etapa2?.dataFim) {
                          setErros({ ...erros, etapa2: { ...erros.etapa2, dataFim: undefined } });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                        erros.etapa2?.dataFim ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {erros.etapa2?.dataFim && (
                      <div className="mt-1 flex items-start gap-2">
                        <i className="ri-error-warning-line text-red-600 text-sm mt-0.5"></i>
                        <p className="text-sm text-red-600">{erros.etapa2.dataFim}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Etapa 3: Produtos/Serviços (antiga etapa 2) */}
          {config.etapaAtual === 3 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Selecionar Produtos e Serviços</h2>
              {campanhaBaseSelecionada && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    ✅ {config.produtosServicosSelecionados.length} produto(s)/serviço(s) importado(s) da campanha base. 
                    Você pode adicionar, editar ou remover itens.
                  </p>
                </div>
              )}
              <ProdutoServicoSelector
                tipo={formBasico.tipo}
                produtosSelecionados={config.produtosServicosSelecionados}
                onProdutosChange={(produtos) => {
                  setConfig({ ...config, produtosServicosSelecionados: produtos });
                  if (erros.etapa3 && produtos.length > 0) {
                    setErros({ ...erros, etapa3: undefined });
                  }
                }}
              />
              {erros.etapa3 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-600 text-lg mt-0.5"></i>
                  <p className="text-sm text-red-800">{erros.etapa3}</p>
                </div>
              )}
            </div>
          )}

          {/* Etapa 4: Participantes (antiga etapa 3) */}
          {config.etapaAtual === 4 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Selecionar Participantes</h2>
              {campanhaBaseSelecionada && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    ✅ {config.participantesSelecionados.length} participante(s) importado(s) da campanha base. 
                    Você pode adicionar ou remover participantes.
                  </p>
                </div>
              )}
              <ParticipantesSelector
                participantesSelecionados={config.participantesSelecionados}
                onParticipantesChange={(participantes) => {
                  setConfig({ ...config, participantesSelecionados: participantes });
                  if (erros.etapa4 && participantes.length > 0) {
                    setErros({ ...erros, etapa4: undefined });
                  }
                }}
              />
              {erros.etapa4 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-600 text-lg mt-0.5"></i>
                  <p className="text-sm text-red-800">{erros.etapa4}</p>
                </div>
              )}
            </div>
          )}

          {/* Etapa 5: Configuração IA (antiga etapa 4) */}
          {config.etapaAtual === 5 && (
            <div>
              {/* Header da Etapa */}
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                    <i className="ri-settings-3-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Configurações de IA</h2>
                    <p className="text-gray-600">Personalize como a inteligência artificial irá gerar sua campanha</p>
                  </div>
                </div>
              </div>

              {campanhaBaseSelecionada && (
                <div className="mb-8 p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-white text-xl"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-green-900 mb-2">
                        💡 Campanha baseada em "{CAMPANHAS_EXISTENTES.find(c => c.id === campanhaBaseSelecionada)?.nome}"
                      </p>
                      <p className="text-sm text-green-800 mb-2">
                        A IA irá <strong>mesclar</strong> os elementos existentes com os novos gerados:
                      </p>
                      <div className="space-y-1 text-sm text-green-800">
                        <p>• Se você <strong>marcar uma opção</strong>, a IA gerará novos elementos e os <strong>adicionará</strong> aos existentes</p>
                        <p>• Se você <strong>desmarcar uma opção</strong>, apenas os elementos existentes serão mantidos</p>
                        <p>• Você poderá editar, adicionar ou remover qualquer elemento na etapa de revisão</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Seção 1: O que Gerar */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">O que a IA deve gerar?</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {/* Metas */}
                  <div
                    onClick={() => {
                      setConfigIA({ ...configIA, gerarMetas: !configIA.gerarMetas });
                      if (erros.etapa5) {
                        setErros({ ...erros, etapa5: undefined });
                      }
                    }}
                    className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 ${
                      configIA.gerarMetas
                        ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg scale-105'
                        : 'border-gray-300 hover:border-green-300 hover:shadow-md'
                    }`}
                  >
                    {configIA.gerarMetas && (
                      <div className="absolute -top-3 -right-3 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                        <i className="ri-check-line text-white text-lg"></i>
                      </div>
                    )}
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all ${
                      configIA.gerarMetas ? 'bg-green-500 shadow-lg' : 'bg-gray-200'
                    }`}>
                      <i className="ri-target-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Metas</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Gerar metas de vendas por produto/serviço e níveis de progressão
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-green-500"></i>
                        <span>Metas por produto</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-green-500"></i>
                        <span>Metas combinadas</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-green-500"></i>
                        <span>5 níveis (Bronze → Diamante)</span>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 rounded-lg text-xs font-semibold">
                        <i className="ri-coin-line"></i>
                        Recompensa: PONTOS
                      </span>
                    </div>
                  </div>

                  {/* Distintivos */}
                  <div
                    onClick={() => {
                      setConfigIA({ ...configIA, gerarDistintivos: !configIA.gerarDistintivos });
                      if (erros.etapa5) {
                        setErros({ ...erros, etapa5: undefined });
                      }
                    }}
                    className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 ${
                      configIA.gerarDistintivos
                        ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-fuchsia-50 shadow-lg scale-105'
                        : 'border-gray-300 hover:border-purple-300 hover:shadow-md'
                    }`}
                  >
                    {configIA.gerarDistintivos && (
                      <div className="absolute -top-3 -right-3 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center shadow-lg">
                        <i className="ri-check-line text-white text-lg"></i>
                      </div>
                    )}
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all ${
                      configIA.gerarDistintivos ? 'bg-purple-500 shadow-lg' : 'bg-gray-200'
                    }`}>
                      <i className="ri-medal-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Distintivos</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Criar distintivos personalizados baseados em conquistas
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-purple-500"></i>
                        <span>Distintivos gerais</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-purple-500"></i>
                        <span>Por produto/serviço</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-purple-500"></i>
                        <span>Conquistas especiais</span>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-lg text-xs font-semibold">
                        <i className="ri-star-line"></i>
                        Recompensa: XP
                      </span>
                    </div>
                  </div>

                  {/* Progressão */}
                  <div
                    onClick={() => {
                      setConfigIA({ ...configIA, gerarProgressao: !configIA.gerarProgressao });
                      if (erros.etapa5) {
                        setErros({ ...erros, etapa5: undefined });
                      }
                    }}
                    className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 ${
                      configIA.gerarProgressao
                        ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-cyan-50 shadow-lg scale-105'
                        : 'border-gray-300 hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    {configIA.gerarProgressao && (
                      <div className="absolute -top-3 -right-3 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                        <i className="ri-check-line text-white text-lg"></i>
                      </div>
                    )}
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all ${
                      configIA.gerarProgressao ? 'bg-blue-500 shadow-lg' : 'bg-gray-200'
                    }`}>
                      <i className="ri-bar-chart-box-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Progressão</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Gerar sistema de níveis e missões diárias, semanais e mensais
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-blue-500"></i>
                        <span>Níveis de XP</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-blue-500"></i>
                        <span>Missões diárias</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-blue-500"></i>
                        <span>Missões semanais/mensais</span>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-xs font-semibold">
                        <i className="ri-star-line"></i>
                        Recompensa: XP
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção 2: Quantidades e Períodos */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Quantidades e Períodos</h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Metas */}
                  <div className={`bg-gradient-to-br from-green-50 to-emerald-50 border-2 rounded-xl p-6 transition-all ${
                    configIA.gerarMetas ? 'border-green-300 shadow-md' : 'border-gray-200 opacity-50'
                  }`}>
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <i className="ri-target-line text-white text-xl"></i>
                      </div>
                      <h4 className="font-bold text-gray-900">Metas</h4>
                    </div>
                    
                    <div className="mb-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Quantidade de Metas
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="3"
                          max="15"
                          value={configIA.quantidadeMetas}
                          onChange={(e) => setConfigIA({ ...configIA, quantidadeMetas: parseInt(e.target.value) })}
                          disabled={!configIA.gerarMetas}
                          className="flex-1 h-3 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed"
                          style={{
                            background: configIA.gerarMetas 
                              ? `linear-gradient(to right, #10b981 0%, #10b981 ${((configIA.quantidadeMetas - 3) / 12) * 100}%, #d1fae5 ${((configIA.quantidadeMetas - 3) / 12) * 100}%, #d1fae5 100%)`
                              : '#e5e7eb'
                          }}
                        />
                        <div className="w-16 h-12 bg-white border-2 border-green-500 rounded-lg flex items-center justify-center">
                          <span className="text-2xl font-bold text-green-600">
                            {configIA.quantidadeMetas}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-2 px-1">
                        <span>Mínimo (3)</span>
                        <span>Máximo (15)</span>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-white/70 rounded-lg border border-green-200">
                      <p className="text-xs text-gray-600">
                        <i className="ri-lightbulb-line text-green-600"></i> <strong>Recomendado:</strong> 5-8 metas para campanhas equilibradas
                      </p>
                    </div>
                  </div>

                  {/* Distintivos */}
                  <div className={`bg-gradient-to-br from-purple-50 to-fuchsia-50 border-2 rounded-xl p-6 transition-all ${
                    configIA.gerarDistintivos ? 'border-purple-300 shadow-md' : 'border-gray-200 opacity-50'
                  }`}>
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                        <i className="ri-medal-line text-white text-xl"></i>
                      </div>
                      <h4 className="font-bold text-gray-900">Distintivos</h4>
                    </div>
                    
                    <div className="mb-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Quantidade de Distintivos
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="3"
                          max="12"
                          value={configIA.quantidadeDistintivos}
                          onChange={(e) => setConfigIA({ ...configIA, quantidadeDistintivos: parseInt(e.target.value) })}
                          disabled={!configIA.gerarDistintivos}
                          className="flex-1 h-3 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed"
                          style={{
                            background: configIA.gerarDistintivos 
                              ? `linear-gradient(to right, #a855f7 0%, #a855f7 ${((configIA.quantidadeDistintivos - 3) / 9) * 100}%, #f3e8ff ${((configIA.quantidadeDistintivos - 3) / 9) * 100}%, #f3e8ff 100%)`
                              : '#e5e7eb'
                          }}
                        />
                        <div className="w-16 h-12 bg-white border-2 border-purple-500 rounded-lg flex items-center justify-center">
                          <span className="text-2xl font-bold text-purple-600">
                            {configIA.quantidadeDistintivos}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-2 px-1">
                        <span>Mínimo (3)</span>
                        <span>Máximo (12)</span>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-white/70 rounded-lg border border-purple-200">
                      <p className="text-xs text-gray-600">
                        <i className="ri-lightbulb-line text-purple-600"></i> <strong>Recomendado:</strong> 6-8 distintivos para engajamento ideal
                      </p>
                    </div>
                  </div>

                  {/* Progressão */}
                  <div className={`bg-gradient-to-br from-blue-50 to-cyan-50 border-2 rounded-xl p-6 transition-all ${
                    configIA.gerarProgressao ? 'border-blue-300 shadow-md' : 'border-gray-200 opacity-50'
                  }`}>
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <i className="ri-bar-chart-box-line text-white text-xl"></i>
                      </div>
                      <h4 className="font-bold text-gray-900">Progressão</h4>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Níveis de XP
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="5"
                          max="20"
                          value={configIA.quantidadeNiveis}
                          onChange={(e) => setConfigIA({ ...configIA, quantidadeNiveis: parseInt(e.target.value) })}
                          disabled={!configIA.gerarProgressao}
                          className="flex-1 h-3 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed"
                          style={{
                            background: configIA.gerarProgressao 
                              ? `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((configIA.quantidadeNiveis - 5) / 15) * 100}%, #dbeafe ${((configIA.quantidadeNiveis - 5) / 15) * 100}%, #dbeafe 100%)`
                              : '#e5e7eb'
                          }}
                        />
                        <div className="w-16 h-12 bg-white border-2 border-blue-500 rounded-lg flex items-center justify-center">
                          <span className="text-2xl font-bold text-blue-600">
                            {configIA.quantidadeNiveis}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-2 px-1">
                        <span>Mínimo (5)</span>
                        <span>Máximo (20)</span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Período das Missões
                      </label>
                      <select
                        value={configIA.periodoMissoes}
                        onChange={(e) => setConfigIA({ ...configIA, periodoMissoes: e.target.value as any })}
                        disabled={!configIA.gerarProgressao}
                        className="w-full px-3 py-2.5 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white font-medium text-sm"
                      >
                        <option value="Completo">📅 Completo (Diárias + Semanais + Mensais)</option>
                        <option value="Apenas Diárias">☀️ Apenas Missões Diárias</option>
                        <option value="Apenas Semanais">📆 Apenas Missões Semanais</option>
                        <option value="Diárias e Semanais">🔄 Diárias e Semanais</option>
                      </select>
                    </div>

                    {configIA.periodoMissoes === 'Completo' && configIA.gerarProgressao && (
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        <div className="bg-white rounded-lg p-2 border-2 border-blue-200">
                          <label className="block text-xs font-semibold text-gray-600 mb-1 text-center">
                            Diárias
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            value={configIA.quantidadeMissoesDiarias}
                            onChange={(e) => setConfigIA({ ...configIA, quantidadeMissoesDiarias: parseInt(e.target.value) || 1 })}
                            className="w-full px-2 py-2 border-2 border-blue-300 rounded-lg text-center font-bold text-lg text-blue-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="bg-white rounded-lg p-2 border-2 border-blue-200">
                          <label className="block text-xs font-semibold text-gray-600 mb-1 text-center">
                            Semanais
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            value={configIA.quantidadeMissoesSemanais}
                            onChange={(e) => setConfigIA({ ...configIA, quantidadeMissoesSemanais: parseInt(e.target.value) || 1 })}
                            className="w-full px-2 py-2 border-2 border-blue-300 rounded-lg text-center font-bold text-lg text-blue-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="bg-white rounded-lg p-2 border-2 border-blue-200">
                          <label className="block text-xs font-semibold text-gray-600 mb-1 text-center">
                            Mensais
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="3"
                            value={configIA.quantidadeMissoesMensais}
                            onChange={(e) => setConfigIA({ ...configIA, quantidadeMissoesMensais: parseInt(e.target.value) || 1 })}
                            className="w-full px-2 py-2 border-2 border-blue-300 rounded-lg text-center font-bold text-lg text-blue-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Seção 3: Dificuldade e Foco */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Dificuldade e Foco das Recompensas</h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Dificuldade */}
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                        <i className="ri-speed-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">Nível de Dificuldade</h4>
                        <p className="text-xs text-gray-600">Define o quão desafiadoras serão as metas</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div
                        onClick={() => setConfigIA({ ...configIA, dificuldade: 'Fácil' })}
                        className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                          configIA.dificuldade === 'Fácil'
                            ? 'border-green-500 bg-green-50 shadow-md scale-105'
                            : 'border-gray-300 hover:border-green-300 hover:bg-green-50/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${
                              configIA.dificuldade === 'Fácil' ? 'bg-green-500 shadow-lg' : 'bg-gray-300'
                            }`}>
                              <i className="ri-emotion-happy-line text-white text-2xl"></i>
                            </div>
                            <div>
                              <h5 className="font-bold text-gray-900">Fácil</h5>
                              <p className="text-xs text-gray-600">Metas acessíveis, ideal para iniciantes</p>
                            </div>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            configIA.dificuldade === 'Fácil'
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-300'
                          }`}>
                            {configIA.dificuldade === 'Fácil' && (
                              <i className="ri-check-line text-white text-sm"></i>
                            )}
                          </div>
                        </div>
                      </div>

                      <div
                        onClick={() => setConfigIA({ ...configIA, dificuldade: 'Equilibrada' })}
                        className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                          configIA.dificuldade === 'Equilibrada'
                            ? 'border-orange-500 bg-orange-50 shadow-md scale-105'
                            : 'border-gray-300 hover:border-orange-300 hover:bg-orange-50/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${
                              configIA.dificuldade === 'Equilibrada' ? 'bg-orange-500 shadow-lg' : 'bg-gray-300'
                            }`}>
                              <i className="ri-scales-line text-white text-2xl"></i>
                            </div>
                            <div>
                              <h5 className="font-bold text-gray-900">Equilibrada</h5>
                              <p className="text-xs text-gray-600">Metas moderadas, recomendado</p>
                            </div>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            configIA.dificuldade === 'Equilibrada'
                              ? 'border-orange-500 bg-orange-500'
                              : 'border-gray-300'
                          }`}>
                            {configIA.dificuldade === 'Equilibrada' && (
                              <i className="ri-check-line text-white text-sm"></i>
                            )}
                          </div>
                        </div>
                      </div>

                      <div
                        onClick={() => setConfigIA({ ...configIA, dificuldade: 'Desafiadora' })}
                        className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                          configIA.dificuldade === 'Desafiadora'
                            ? 'border-red-500 bg-red-50 shadow-md scale-105'
                            : 'border-gray-300 hover:border-red-300 hover:bg-red-50/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${
                              configIA.dificuldade === 'Desafiadora' ? 'bg-red-500 shadow-lg' : 'bg-gray-300'
                            }`}>
                              <i className="ri-fire-line text-white text-2xl"></i>
                            </div>
                            <div>
                              <h5 className="font-bold text-gray-900">Desafiadora</h5>
                              <p className="text-xs text-gray-600">Metas ambiciosas, para experientes</p>
                            </div>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            configIA.dificuldade === 'Desafiadora'
                              ? 'border-red-500 bg-red-500'
                              : 'border-gray-300'
                          }`}>
                            {configIA.dificuldade === 'Desafiadora' && (
                              <i className="ri-check-line text-white text-sm"></i>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Foco */}
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                        <i className="ri-focus-3-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">Foco das Recompensas</h4>
                        <p className="text-xs text-gray-600">Tipo de recompensa priorizado pela IA</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div
                        onClick={() => setConfigIA({ ...configIA, focoRecompensas: 'Pontos' })}
                        className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                          configIA.focoRecompensas === 'Pontos'
                            ? 'border-green-500 bg-green-50 shadow-md scale-105'
                            : 'border-gray-300 hover:border-green-300 hover:bg-green-50/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${
                              configIA.focoRecompensas === 'Pontos' ? 'bg-green-500 shadow-lg' : 'bg-gray-300'
                            }`}>
                              <i className="ri-coin-line text-white text-2xl"></i>
                            </div>
                            <div>
                              <h5 className="font-bold text-gray-900">Foco em Pontos</h5>
                              <p className="text-xs text-gray-600">Prioriza recompensas monetárias</p>
                            </div>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            configIA.focoRecompensas === 'Pontos'
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-300'
                          }`}>
                            {configIA.focoRecompensas === 'Pontos' && (
                              <i className="ri-check-line text-white text-sm"></i>
                            )}
                          </div>
                        </div>
                      </div>

                      <div
                        onClick={() => setConfigIA({ ...configIA, focoRecompensas: 'XP' })}
                        className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                          configIA.focoRecompensas === 'XP'
                            ? 'border-purple-500 bg-purple-50 shadow-md scale-105'
                            : 'border-gray-300 hover:border-purple-300 hover:bg-purple-50/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${
                              configIA.focoRecompensas === 'XP' ? 'bg-purple-500 shadow-lg' : 'bg-gray-300'
                            }`}>
                              <i className="ri-star-line text-white text-2xl"></i>
                            </div>
                            <div>
                              <h5 className="font-bold text-gray-900">Foco em XP</h5>
                              <p className="text-xs text-gray-600">Prioriza gamificação e engajamento</p>
                            </div>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            configIA.focoRecompensas === 'XP'
                              ? 'border-purple-500 bg-purple-500'
                              : 'border-gray-300'
                          }`}>
                            {configIA.focoRecompensas === 'XP' && (
                              <i className="ri-check-line text-white text-sm"></i>
                            )}
                          </div>
                        </div>
                      </div>

                      <div
                        onClick={() => setConfigIA({ ...configIA, focoRecompensas: 'Balanceado' })}
                        className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                          configIA.focoRecompensas === 'Balanceado'
                            ? 'border-blue-500 bg-blue-50 shadow-md scale-105'
                            : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${
                              configIA.focoRecompensas === 'Balanceado' ? 'bg-blue-500 shadow-lg' : 'bg-gray-300'
                            }`}>
                              <i className="ri-balance-line text-white text-2xl"></i>
                            </div>
                            <div>
                              <h5 className="font-bold text-gray-900">Balanceado</h5>
                              <p className="text-xs text-gray-600">Equilíbrio entre pontos e XP</p>
                            </div>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            configIA.focoRecompensas === 'Balanceado'
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-gray-300'
                          }`}>
                            {configIA.focoRecompensas === 'Balanceado' && (
                              <i className="ri-check-line text-white text-sm"></i>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Erro de validação */}
              {erros.etapa5 && (
                <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3 shadow-sm">
                  <i className="ri-error-warning-line text-red-600 text-2xl mt-0.5"></i>
                  <p className="text-sm text-red-800 font-medium">{erros.etapa5}</p>
                </div>
              )}

              {/* Resumo Final */}
              <div className="bg-gradient-to-br from-gray-50 to-slate-100 border-2 border-gray-300 rounded-xl p-6 shadow-lg">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl flex items-center justify-center shadow-md">
                    <i className="ri-file-list-3-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-1">Resumo da Configuração</h4>
                    <p className="text-sm text-gray-600">Confira o que será gerado pela IA</p>
                  </div>
                </div>
                
                {!configIA.gerarMetas && !configIA.gerarDistintivos && !configIA.gerarProgressao ? (
                  <div className="p-4 bg-orange-50 border-2 border-orange-300 rounded-lg">
                    <div className="flex items-center gap-3">
                      <i className="ri-alert-line text-orange-600 text-2xl"></i>
                      <p className="text-sm text-orange-800 font-medium">
                        ⚠️ Selecione pelo menos uma opção para continuar
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {configIA.gerarMetas && (
                      <div className="bg-white border-2 border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                            <i className="ri-target-line text-white"></i>
                          </div>
                          <h5 className="font-bold text-gray-900">Metas</h5>
                        </div>
                        <p className="text-sm text-gray-700">
                          <strong className="text-green-600">{configIA.quantidadeMetas}</strong> metas de vendas com recompensas em pontos
                        </p>
                      </div>
                    )}
                    {configIA.gerarDistintivos && (
                      <div className="bg-white border-2 border-purple-200 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                            <i className="ri-medal-line text-white"></i>
                          </div>
                          <h5 className="font-bold text-gray-900">Distintivos</h5>
                        </div>
                        <p className="text-sm text-gray-700">
                          <strong className="text-purple-600">{configIA.quantidadeDistintivos}</strong> distintivos personalizados com recompensas em XP
                        </p>
                      </div>
                    )}
                    {configIA.gerarProgressao && (
                      <div className="bg-white border-2 border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                            <i className="ri-bar-chart-box-line text-white"></i>
                          </div>
                          <h5 className="font-bold text-gray-900">Progressão</h5>
                        </div>
                        <p className="text-sm text-gray-700">
                          <strong className="text-blue-600">{configIA.quantidadeNiveis}</strong> níveis e missões <strong>{configIA.periodoMissoes.toLowerCase()}</strong>
                        </p>
                      </div>
                    )}
                    <div className="bg-white border-2 border-orange-200 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                          <i className="ri-settings-3-line text-white"></i>
                        </div>
                        <h5 className="font-bold text-gray-900">Configurações</h5>
                      </div>
                      <p className="text-sm text-gray-700">
                        Dificuldade: <strong className="text-orange-600">{configIA.dificuldade}</strong><br/>
                        Foco: <strong className="text-orange-600">{configIA.focoRecompensas}</strong>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Etapa 6: Geração IA (antiga etapa 5) */}
          {config.etapaAtual === 6 && (
            <div className="text-center py-12">
              {gerando ? (
                <div>
                  <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-orange-500 border-t-transparent mb-4"></div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Gerando Campanha com IA...</h2>
                  <p className="text-gray-600 mb-4">
                    {campanhaBaseSelecionada 
                      ? 'Mesclando dados existentes e gerando novos elementos com IA'
                      : 'Analisando produtos, participantes e aplicando a Matriz Peso × Perfil'
                    }
                  </p>
                  <div className="max-w-md mx-auto space-y-2 text-sm text-gray-500">
                    {configIA.gerarMetas && (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse"></div>
                        <span>Gerando metas de vendas...</span>
                      </div>
                    )}
                    {configIA.gerarDistintivos && (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 bg-purple-500 rounded-full animate-pulse"></div>
                        <span>Criando distintivos personalizados...</span>
                      </div>
                    )}
                    {configIA.gerarProgressao && (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
                        <span>Gerando níveis e missões...</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-brain-line text-4xl text-white"></i>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Pronto para Gerar!</h2>
                  <p className="text-gray-600 mb-6">
                    {campanhaBaseSelecionada 
                      ? 'A IA irá mesclar os dados existentes com novos elementos gerados'
                      : 'A IA irá criar automaticamente os elementos selecionados'
                    }
                  </p>
                  <button
                    onClick={handleGerarCampanha}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all font-semibold flex items-center gap-2 mx-auto whitespace-nowrap"
                  >
                    <i className="ri-magic-line"></i>
                    Gerar Campanha com IA
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Etapa 7: Revisão EDITÁVEL (antiga etapa 6) */}
          {config.etapaAtual === 7 && config.campanhaGerada && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Revisão e Ajustes da Campanha</h2>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                  <i className="ri-edit-line text-blue-600"></i>
                  <span className="text-sm text-blue-800 font-medium">Modo de Edição Ativo</span>
                </div>
              </div>
              
              {campanhaBaseSelecionada && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <i className="ri-check-circle-line text-green-600 text-xl mt-0.5"></i>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-green-900 mb-1">
                        ✅ Campanha gerada com sucesso baseada em "{CAMPANHAS_EXISTENTES.find(c => c.id === campanhaBaseSelecionada)?.nome}"
                      </p>
                      <p className="text-sm text-green-800">
                        Os elementos existentes foram mesclados com os novos gerados pela IA. Revise e ajuste conforme necessário.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-blue-600 text-xl mt-0.5"></i>
                  <div className="flex-1">
                    <p className="text-sm text-blue-800 font-medium mb-1">💡 Dica: Você pode editar todos os valores!</p>
                    <p className="text-sm text-blue-700">
                      Clique no ícone de edição para ajustar metas, distintivos, missões e recompensas. 
                      Os custos serão recalculados automaticamente após cada alteração.
                    </p>
                  </div>
                </div>
              </div>
              <EditableSimulacaoPreview 
                campanha={config.campanhaGerada} 
                onCampanhaChange={handleCampanhaChange}
                campanhaBaseId={campanhaBaseSelecionada}
              />
            </div>
          )}

          {/* NOVA Etapa 8: Análise Inteligente */}
          {config.etapaAtual === 8 && config.campanhaGerada && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Análise Inteligente da IA</h2>
              <AnaliseInteligente campanha={config.campanhaGerada} />
            </div>
          )}

          {/* Etapa 9: Análise de Custos (antiga etapa 7) */}
          {config.etapaAtual === 9 && config.campanhaGerada && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Análise de Custos e Complexidade</h2>
              <CustoAnalyzer campanha={config.campanhaGerada} />
            </div>
          )}

          {/* Etapa 10: Sugestões (antiga etapa 8) */}
          {config.etapaAtual === 10 && config.campanhaGerada && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Sugestões Evolutivas da IA</h2>
              <IAInsights campanha={config.campanhaGerada} />
            </div>
          )}
        </div>

        {/* Botões de Navegação */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={handleVoltarEtapa}
            disabled={config.etapaAtual === 1 || gerando}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar
          </button>

          <div className="flex gap-3">
            {config.etapaAtual === 10 && config.campanhaGerada && (
              <>
                <button
                  onClick={handleSalvarSimulacao}
                  disabled={salvando}
                  className="px-6 py-2 border border-orange-500 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-save-line"></i>
                  {salvando ? 'Salvando...' : 'Salvar Simulação'}
                </button>
                <button
                  onClick={handleCriarCampanha}
                  className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-check-line"></i>
                  Criar Campanha
                </button>
              </>
            )}
            {config.etapaAtual < 10 && (
              <button
                onClick={handleProximaEtapa}
                disabled={gerando}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
              >
                {config.etapaAtual === 6 ? 'Gerar com IA' : 'Próximo'}
                <i className="ri-arrow-right-line"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
